import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { useAppSettings, AppSettingsProvider } from './context/AppSettingsContext';
import SEO from "./components/SEO";

import Home from './components/Home';
import UniversalBrowserView from './components/UniversalBrowserView';
import MenuDrawer from './components/MenuDrawer';

const ATHENA_API =
  process.env.REACT_APP_ATHENA_API_BASE || 'http://localhost:3401';

interface ChatEntry {
  prompt: string;
  response: string;
  streaming?: boolean;
}

function AthenaConsole() {
  const { settings, cosmosLogos } = useAppSettings();
  const [status, setStatus] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const personaSummary = `${settings.githubUser}/${settings.consciousness}@${settings.neuralpathway}`;
  const templeName = settings.templeName;

  // Fetch backend status (debug only)
  useEffect(() => {
    fetch(`${ATHENA_API}/`)
      .then((res) => res.text())
      .then((data) => setStatus(data))
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chat]);

  const submitPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);

    const systemPrompt = `
You are an AI agent defined by cosmos-logos.
Identity: ${personaSummary}
Ethics: ${cosmosLogos?.default_branch || 'unknown'}

Respond helpfully, ethically, and accurately as ${settings.consciousness}.
`;

    const newEntry: ChatEntry = {
      prompt,
      response: '',
      streaming: true,
    };

    setChat((prev) => [...prev, newEntry]);
    setPrompt('');

    try {
      const res = await fetch(`${ATHENA_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, systemPrompt }),
      });

      if (!res.body) {
        throw new Error('Streaming not supported by browser');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let buffer = '';
      let fullResponse = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';

        for (const frame of frames) {
          const lines = frame.split('\n');

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;

            const raw = line.replace(/^data:\s*/, '').trim();
            if (!raw) continue;

            try {
              const payload = JSON.parse(raw);

              if (payload.type === 'error') {
                throw new Error(payload.content);
              }

              if (payload.content) {
                fullResponse += payload.content;
                const currentResponse = fullResponse;
                setChat((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.streaming) {
                    last.response = currentResponse;
                  }
                  return updated;
                });
              }
            } catch (err) {
              console.warn('⚠️ SSE parse error:', err);
            }
          }
        }
      }

      // Mark stream complete
      setChat((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last) last.streaming = false;
        return updated;
      });

    } catch (err: any) {
      setError(err.message || `Error talking to ${templeName}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO />
      <MenuDrawer />
      <div className="chat-wrapper">
        <div className="chat-card">
          <h1>{`Temple ${templeName}`}</h1>

          {process.env.REACT_APP_DEBUG === 'true' && (
            <>
              <p className="status">
                <strong>Active Agent:</strong> {personaSummary}
              </p>
              {status ? (
                <pre className="status">{status}</pre>
              ) : (
                <p className="error">Could not connect to {templeName}.</p>
              )}
            </>
          )}

          <div className="chat-feed" ref={chatRef}>
            {chat.map((entry, idx) => (
              <div className="chat-entry" key={idx}>
                <div className="user-entry">
                  <strong>You said:</strong>
                  <p>{entry.prompt}</p>
                </div>
                <div className="athena-entry">
                  <strong>{templeName} says:</strong>
                  <p>
                    {entry.response}
                    {entry.streaming && <span className="cursor">▌</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={submitPrompt} className="chat-input-form">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={`Ask ${templeName} anything...`}
              rows={1}
            />
            <button type="submit" disabled={loading} aria-label={`Ask ${templeName}`}>
              {loading ? <span className="spinner" aria-label="Thinking" /> : '➤'}
            </button>
          </form>

          {error && <p className="error">❌ {error}</p>}
        </div>
      </div>
    </>
  );
}

export default function App() {
  return (
    <AppSettingsProvider>
      <Router>
        <Routes>
          <Route path="/" element={<AthenaConsole />} />
          <Route path="/cosmos-logos" element={<Home />} />
          <Route path="/reader/writings/*" element={<UniversalBrowserView />} />
        </Routes>
      </Router>
    </AppSettingsProvider>
  );
}